import { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useHubTabs } from '@/lib/permisos';
import {
  Megaphone, Users, Home, Zap, Radio,
  ChevronDown, ChevronUp, Calendar, Truck, Contact,
} from 'lucide-react';
import api from '@/lib/api';
import { useLocale } from '@/contexts/LocaleContext';

/**
 * S176 — Marketing = captación y crecimiento (QR, listas, automáticos, capturas).
 * Email masivo y WhatsApp masivo son módulos PROPIOS en el menú lateral.
 * Aquí no se mezclan con mailing.
 */
const PRIMARY_TABS = [
  { key: 'home', path: '/tenant/marketing', labelKey: 'mkt.home', icon: Home, end: true, module: 'marketing' },
  { key: 'origenes', path: '/tenant/marketing/origenes', labelKey: 'mkt.origins', icon: Radio, module: 'lead_sources' },
  { key: 'segments', path: '/tenant/marketing/segments', labelKey: 'mkt.lists', icon: Users, module: 'audiences' },
  { key: 'automations', path: '/tenant/marketing/automations', labelKey: 'mkt.automations', icon: Zap, module: 'marketing' },
];

const ADVANCED_TABS = [
  { key: 'campaigns', path: '/tenant/marketing/campaigns', labelKey: 'mkt.campaigns', icon: Megaphone, module: 'marketing' },
  { key: 'contacts', path: '/tenant/marketing/contacts', labelKey: 'mkt.contacts', icon: Contact, module: 'contacts' },
  { key: 'schedules', path: '/tenant/marketing/schedules', labelKey: 'mkt.schedules', icon: Calendar, module: 'campaign_schedules' },
  { key: 'delivery', path: '/tenant/marketing/delivery', labelKey: 'mkt.delivery', icon: Truck, module: 'campaign_delivery' },
];

export default function MarketingHubLayout() {
  const location = useLocation();
  const { t } = useLocale();
  const { tabs: primaryTabs } = useHubTabs(PRIMARY_TABS);
  const { tabs: advancedTabs } = useHubTabs(ADVANCED_TABS);
  const [limits, setLimits] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(() =>
    advancedTabs.some(t => location.pathname.startsWith(t.path))
  );

  useEffect(() => {
    api.get('/tenant/marketing/limits').then(r => setLimits(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (advancedTabs.some(t => location.pathname.startsWith(t.path))) {
      setShowAdvanced(true);
    }
  }, [location.pathname]);

  if (limits.campaigns_enabled === false) {
    return (
      <div className="p-8 text-center text-sm text-[#64748B]">
        Marketing no habilitado en tu plan.
      </div>
    );
  }

  const tabClass = (active) =>
    `flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${
      active
        ? 'border-[#F72585] text-[#0F172A]'
        : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
    }`;

  return (
    <div className="space-y-4" data-testid="marketing-hub">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[#F72585]" /> {t('mkt.title')}
          </h1>
          <p className="text-sm text-[#64748B] max-w-2xl">{t('mkt.sub')}</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[#E2E8F0] overflow-x-auto" data-testid="marketing-hub-tabs">
        {primaryTabs.map(tab => (
          <NavLink
            key={tab.key}
            to={tab.path}
            end={tab.end}
            data-testid={`marketing-tab-${tab.key}`}
            className={({ isActive }) => {
              const active = isActive
                || (tab.key === 'origenes' && (location.pathname.includes('origenes') || location.pathname.includes('qr-campaigns') || location.pathname.includes('lead-capture')))
                || (tab.key === 'automations' && location.pathname.includes('automations'))
                || (tab.key === 'segments' && location.pathname.includes('segments'));
              return tabClass(active);
            }}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.labelKey ? t(tab.labelKey) : tab.label}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setShowAdvanced(v => !v)}
          className={tabClass(showAdvanced && advancedTabs.some(t => location.pathname.startsWith(t.path)))}
          data-testid="marketing-tab-advanced"
        >
          {showAdvanced ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {t('mkt.more')}
        </button>
      </div>

      {showAdvanced && (
        <div className="flex gap-1 overflow-x-auto pb-1" data-testid="marketing-advanced-tabs">
          {advancedTabs.map(tab => (
            <NavLink
              key={tab.key}
              to={tab.path}
              data-testid={`marketing-adv-${tab.key}`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-full border whitespace-nowrap ${
                  isActive || location.pathname.startsWith(tab.path)
                    ? 'border-[#F72585] bg-[#FFF5F9] text-[#0F172A] font-medium'
                    : 'border-[#E2E8F0] text-[#64748B] hover:border-[#CBD5E1]'
                }`
              }
            >
              <tab.icon className="w-3 h-3" />
              {tab.labelKey ? t(tab.labelKey) : tab.label}
            </NavLink>
          ))}
        </div>
      )}

      <Outlet />
    </div>
  );
}
